import { BehaviorSubject, Subject, Observable, defer, race, concat, combineLatest, EMPTY, merge } from 'rxjs';
import {
    map,
    tap,
    skipWhile,
    skip,
    take,
    delay,
    mergeScan,
    startWith,
    shareReplay,
    takeUntil,
    finalize,
    distinctUntilChanged,
    mergeMap,
} from 'rxjs/operators';

const DEFAULT_SIZE = 30;

function progressTo(subject$: BehaviorSubject<number> | (() => BehaviorSubject<number>)) {
    const getSub = () => (subject$ instanceof BehaviorSubject ? subject$ : subject$());
    return <T>(src$: Observable<T>): Observable<T> =>
        defer(() => {
            getSub().next(getSub().getValue() + 1);
            return src$.pipe(finalize(() => getSub().next(getSub().getValue() - 1)));
        });
}

function getObservable<T>(obsOrFn: Observable<T> | (() => Observable<T>)): Observable<T> {
    return typeof obsOrFn === 'function' ? defer(obsOrFn) : obsOrFn;
}

function inProgressFrom(
    progress: Observable<number> | Array<Observable<number>> | (() => Observable<number>),
    main?: Observable<unknown> | (() => Observable<unknown>),
    init = true
): Observable<boolean> {
    const progresses = (Array.isArray(progress) ? progress : [progress]).map((p) => getObservable(p));
    return merge(combineLatest(progresses), main ? getObservable(main).pipe(mergeMap(() => EMPTY)) : EMPTY).pipe(
        map((ps) => (ps as unknown[]).some((p) => !!p)),
        delay(0), // make async to bypass Angular change detection
        startWith(init),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 })
    );
}

export interface Action<Params> {
    type: 'load' | 'reload' | 'more' | 'init';
    params?: Params;
    size?: number;
}

export interface Result<ResultItem, ContinuationToken = string> {
    result: ResultItem[];
    continuationToken?: ContinuationToken;
}

export interface FetchOptions<ContinuationToken = string> {
    size: number;
    continuationToken?: ContinuationToken;
}

export interface LoadOptions {
    size?: number;
}

export interface Accumulator<ResultItem, Params, ContinuationToken> {
    result: ResultItem[];
    size: number;
    params?: Params;
    continuationToken?: ContinuationToken;
}

export abstract class FetchSuperclass<ResultItem, Params = void, ContinuationToken = string> {
    result$: Observable<ResultItem[]>;
    hasMore$: Observable<boolean>;
    isLoading$: Observable<boolean>;

    private fetch$ = new BehaviorSubject<Action<Params>>({ type: 'init' });
    private progress$ = new BehaviorSubject<number>(0);
    private state$: Observable<Accumulator<ResultItem, Params, ContinuationToken>>;
    private skipPrevious$ = new Subject<boolean>();

    constructor() {
        this.result$ = defer(() => this.state$).pipe(map(({ result }) => result));
        this.hasMore$ = defer(() => this.state$).pipe(map(({ continuationToken }) => !!continuationToken));
        this.isLoading$ = inProgressFrom(
            () => this.progress$,
            () => this.state$
        );

        this.state$ = defer(() => {
            const resAction$ = this.fetch$.pipe(skipWhile((action) => action.type !== 'load'));
            return race(concat(resAction$.pipe(take(1), delay(1000)), resAction$), resAction$.pipe(skip(1)));
        }).pipe(
            tap((action) => {
                if (action.type === 'load') {
                    this.skipPrevious$.next(true);
                }
            }),
            mergeScan(
                (acc, action) => {
                    const params = action.type === 'load' ? action.params : acc.params;
                    const size = action.size ?? acc.size;
                    const continuationToken = action.type === 'more' ? acc.continuationToken : undefined;
                    let observable = this.fetch(params, { size, continuationToken }).pipe(
                        map(({ result, continuationToken }) => ({
                            params,
                            result:
                                action.type === 'load' || action.type === 'reload'
                                    ? result
                                    : [...(acc.result ?? []), ...result],
                            size,
                            continuationToken,
                        })),
                        progressTo(this.progress$),
                        takeUntil(this.skipPrevious$)
                    );

                    if (action.type === 'load') {
                        observable = observable.pipe(
                            startWith({
                                params,
                                result: [],
                                size,
                                continuationToken: undefined,
                            })
                        );
                    }

                    return observable;
                },
                {
                    size: DEFAULT_SIZE,
                    result: [],
                } as Accumulator<ResultItem, Params, ContinuationToken>,
                1
            ),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    load(params: Params, options: LoadOptions = {}) {
        this.fetch$.next({ type: 'load', params, size: options.size });
    }

    reload(options: LoadOptions = {}) {
        this.fetch$.next({ type: 'reload', size: options.size });
    }

    more() {
        this.fetch$.next({ type: 'more' });
    }

    protected abstract fetch(
        params: Params,
        options: FetchOptions<ContinuationToken>
    ): Observable<Result<ResultItem, ContinuationToken>>;
}
